import RpiPriorLayerSelector from "./RpiPriorLayerSelector";
import {
  useRpiCalculation,
  formatLayerName,
  MatchType,
} from "./useRpiCalculation";
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import * as numbers from "common/numbers";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as stateSelectors from "store/selectors/stateSelectors";

/**
 * Gets a human-readable description of how the prior layer was matched
 */
const getMatchTypeDescription = (matchType) => {
  switch (matchType) {
    case MatchType.EXACT:
      return "Exact match";
    case MatchType.CLOSE:
      return "Close match (~10%)";
    case MatchType.BOUND_FALLBACK:
      return "Bound layer (fallback)";
    case MatchType.MANUAL:
      return "Manually selected";
    case MatchType.RENEWED:
      return "Renewed from layer";
    default:
      return "Unknown";
  }
};

/**
 * RPI Cell component that displays RPI value for a specific layer
 * Shows which prior layer is being used and allows manual selection
 *
 * @param {Object} props - Component props
 * @param {string} props.layerId - ID of the layer to calculate RPI for
 * @returns {JSX.Element} RPI cell component
 */
const RpiCell = ({ layerId }) => {
  const { validation, getRpiForLayer } = useRpiCalculation();

  const priorState = useSelector(stateSelectors.selectPriorState);
  const [selectorOpen, setSelectorOpen] = useState(false);

  const { rpi, priorLayer, matchType } = useMemo(() => {
    return getRpiForLayer(layerId);
  }, [layerId, getRpiForLayer]);

  /**
   * Gets the combined ratio for a prior layer
   */
  const getPriorLayerCombinedRatio = (layer) => {
    if (!layer || !priorState) {
      return null;
    }
    const priorAnalyticsLayers =
      priorState?.analytics?.programPricing?.values?.layers || {};
    return priorAnalyticsLayers[layer.id]?.GGLRCombinedRatioFOT || null;
  };

  if (!validation.shouldShow) {
    return <></>;
  }

  // If RPI is null, show a button to select a prior layer
  if (rpi === null) {
    const priorCombinedRatio = priorLayer
      ? getPriorLayerCombinedRatio(priorLayer)
      : null;
    const hasPriorCombinedRatio =
      priorCombinedRatio != null && priorCombinedRatio !== 0;

    const noMatchTooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography variant={"body2"} sx={{ fontWeight: "bold", mb: 0.5 }}>
          {"No RPI calculated"}
        </Typography>
        {priorLayer && (
          <>
            <Typography variant={"caption"} component={"div"} sx={{ mt: 0.5 }}>
              {"Prior layer: "}
              <Typography
                component={"span"}
                variant={"caption"}
                sx={{ fontWeight: "bold" }}
              >
                {formatLayerName(priorLayer)}
              </Typography>
            </Typography>
            {hasPriorCombinedRatio ? (
              <Typography
                variant={"caption"}
                component={"div"}
                sx={{ mt: 0.5 }}
              >
                {`Prior Combined Ratio: ${numbers.percentage(1)(
                  priorCombinedRatio
                )}`}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                <WarningIcon sx={{ fontSize: 14, color: "warning.main" }} />
                <Typography variant={"caption"} color={"warning.main"}>
                  {"No combined ratio data for prior layer"}
                </Typography>
              </Box>
            )}
          </>
        )}
        <Typography variant={"caption"} component={"div"} sx={{ mt: 0.5 }}>
          {priorLayer
            ? "Unable to calculate RPI. Click to select a different prior layer."
            : "No prior layer matched. Click to manually select a prior layer for RPI calculation."}
        </Typography>
      </Box>
    );

    return (
      <>
        <Tooltip title={noMatchTooltipContent} placement={"left"}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              justifyContent: "flex-end",
            }}
          >
            <Typography
              variant={"body2"}
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              {"—"}
            </Typography>
            <IconButton
              size={"small"}
              onClick={() => setSelectorOpen(true)}
              sx={{
                ml: 0.5,
                padding: "2px",
                opacity: 0.6,
                "&:hover": {
                  opacity: 1,
                },
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Tooltip>
        <RpiPriorLayerSelector
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          layerId={layerId}
          currentPriorLayerId={priorLayer?.id}
          currentMatchType={matchType}
        />
      </>
    );
  }

  const priorCombinedRatio = priorLayer
    ? getPriorLayerCombinedRatio(priorLayer)
    : null;
  const hasPriorCombinedRatio =
    priorCombinedRatio != null && priorCombinedRatio !== 0;

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant={"body2"} sx={{ fontWeight: "bold", mb: 0.5 }}>
        {"RPI: "}
        {numbers.percentage(2)(rpi)}
        {" ("}
        {rpi > 1 ? "Good" : "Poor"}
        {" Performance)"}
      </Typography>
      <Typography variant={"caption"} component={"div"}>
        {"Compared to prior layer:"}
      </Typography>
      <Typography
        variant={"caption"}
        component={"div"}
        sx={{ fontWeight: "bold" }}
      >
        {formatLayerName(priorLayer)}
      </Typography>
      {hasPriorCombinedRatio ? (
        <Typography variant={"caption"} component={"div"} sx={{ mt: 0.5 }}>
          {`Prior Combined Ratio: ${numbers.percentage(1)(priorCombinedRatio)}`}
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.5,
          }}
        >
          <WarningIcon sx={{ fontSize: 14, color: "warning.main" }} />
          <Typography variant={"caption"} color={"warning.main"}>
            {"No combined ratio data for prior layer"}
          </Typography>
        </Box>
      )}
      <Typography
        variant={"caption"}
        component={"div"}
        sx={{ fontStyle: "italic", mt: 0.5 }}
      >
        {getMatchTypeDescription(matchType)}
      </Typography>
      <Typography
        variant={"caption"}
        component={"div"}
        sx={{ mt: 0.5, color: "grey.400" }}
      >
        {"Click edit icon to change"}
      </Typography>
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipContent} placement={"left"}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
          }}
        >
          {rpi > 1 ? (
            <ArrowUpIcon
              sx={{
                color: "#2e7d32",
                fontSize: 16,
              }}
            />
          ) : (
            <ArrowDownIcon
              sx={{
                color: "#d32f2f",
                fontSize: 16,
              }}
            />
          )}
          <Typography
            component={"span"}
            sx={{
              color: rpi > 1 ? "#2e7d32 !important" : "#d32f2f !important",
            }}
          >
            {numbers.percentage(2)(rpi)}
          </Typography>
          <IconButton
            size={"small"}
            onClick={() => setSelectorOpen(true)}
            sx={{
              ml: 0.5,
              padding: "2px",
              opacity: 0.6,
              "&:hover": {
                opacity: 1,
              },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Tooltip>
      <RpiPriorLayerSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        layerId={layerId}
        currentPriorLayerId={priorLayer?.id}
        currentMatchType={matchType}
      />
    </>
  );
};

export default RpiCell;
